import React from "react";
import { DateTime } from "luxon";

import { useRetrieveWbsMeta } from "@planzone/view-models";

import { sort as sortData } from "@common/sort";
import { useUtils } from "@common/treeToggle";
import { filter as filterData } from "@common/filter";
import { Context } from "../../context";
import { ProjectCtx } from "../../projectCtx";
import { renderWithTreeCtx } from "../../utils";
import { Views } from "../../api/views";
import { Props } from "./types";
import { Element } from "./element";
import styles from "./styles.scss";

// Stable empty array: elements with no constraints previously received a
// fresh `[]` from .filter() on every render, which alone defeated
// React.memo(Element) for the whole scene.
const NO_CONSTRAINTS: Props["constraints"] = [];

function ElementsOverlay({
  dates,
  filter,
  sort,
  constraints,
  graphRef,
  projects: p,
}: Props) {
  const { meta } = useRetrieveWbsMeta(p);
  const { isOpen } = useUtils();

  // Was recomputed inline on every render: new array identity for `projects`
  // and for every `project` prop below it.
  const projects = React.useMemo(
    () =>
      sortData(
        sort,
        filterData(p, filter, (project) => ({ project })),
        (project) => ({
          name: project.name,
          startDate: project.rootList.startDate,
          endDate: project.rootList.endDate,
        }),
      ),
    [p, filter, sort],
  );

  // One pass over constraints instead of one .filter() per element per
  // render (O(E + C) instead of O(E × C)), and — the part that matters for
  // memo — a per-uuid array whose identity only changes when `constraints`
  // itself does.
  const constraintsByUuid = React.useMemo(() => {
    const map = new Map<string, Props["constraints"][number][]>();
    const push = (uuid: string, c: Props["constraints"][number]) => {
      const list = map.get(uuid);
      list ? list.push(c) : map.set(uuid, [c]);
    };
    for (const c of constraints) {
      push(c.originEntityUuid, c);
      if (c.targetEntityUuid !== c.originEntityUuid) {
        push(c.targetEntityUuid, c);
      }
    }
    return map;
  }, [constraints]);

  // The old `meta[uuid] || { ...zeros }` fallback minted a brand-new object
  // for every meta-less element on every render. Cache fallbacks per uuid;
  // the cache resets when `meta` itself changes, so real data still wins.
  type ElementMeta = NonNullable<(typeof meta)[string]>;
  const metaFallbacks = React.useMemo(
    () => new Map<string, ElementMeta>(),
    [meta],
  );
  const getMeta = (uuid: string): ElementMeta => {
    const fromStore = meta[uuid];
    if (fromStore) return fromStore;
    let fallback = metaFallbacks.get(uuid);
    if (!fallback) {
      fallback = {
        allocatedHours: 0,
        attachmentCount: 0,
        commentCount: 0,
        elementCount: 0,
        duration: 0,
        percentComplete: 0,
        spentHours: 0,
        uuid,
      } as ElementMeta;
      metaFallbacks.set(uuid, fallback);
    }
    return fallback;
  };

  // renderWithTreeCtx rebuilds its per-depth ctx literals on every walk, so
  // `ctx` failed Element's memo even when its contents were identical.
  // Reuse the previous object per uuid while minDate/maxDate are unchanged.
  const ctxCache = React.useRef(
    new Map<string, { minDate: string; maxDate: string }>(),
  );
  const stableCtx = (
    uuid: string,
    ctx: { minDate: string; maxDate: string },
  ) => {
    const prev = ctxCache.current.get(uuid);
    if (prev && prev.minDate === ctx.minDate && prev.maxDate === ctx.maxDate) {
      return prev;
    }
    ctxCache.current.set(uuid, ctx);
    return ctx;
  };

  const userTimezone = DateTime.local().zoneName;
  const { cell, currentView } = React.useContext(Context);
  // DateTime.now() fallback was a fresh identity per render when dates was
  // empty; harmless-looking, but it's an Element prop.
  const minDate = React.useMemo(
    () => dates[0] || DateTime.now().setZone(userTimezone),
    [dates, userTimezone],
  );
  const offsets = React.useRef<{
    [Key in (typeof projects)[number]["uuid"]]: number;
  }>({});
  const paddingTop =
    cell.height +
    1 +
    (currentView === Views.DAILY
      ? cell.height * 2
      : cell.height + cell.height * (1 / 3));

  return (
    <div className={styles.elementsOverlay}>
      {projects.map((project, projectIndex) => (
        <ProjectCtx
          key={project.uuid}
          project={project}
          filter={filter}
          sort={sort}
          isMultiProject={projects.length > 1}
          isFlagRendered={false}
        >
          {(gantt) => {
            // Render-phase ref write, preserved as-is: each project's offset
            // depends on the previous project's gantt length, which only
            // exists inside the previous render prop. Restructuring this is
            // out of scope here; it is idempotent per render pass.
            const offset = projects[projectIndex - 1]
              ? offsets.current[projects[projectIndex - 1].uuid]
              : 0;
            offsets.current[project.uuid] = offset + gantt.length;

            return (
              <React.Fragment key={projectIndex}>
                {renderWithTreeCtx(
                  gantt,
                  isOpen,
                  (projectElement, ctx, index) => (
                    <div
                      id={`projectElement:${projectElement.uuid}`}
                      key={projectElement.uuid}
                      className={styles.element}
                      style={{
                        top: cell.height * (index + offset) + paddingTop + 45,
                        height: cell.height,
                        zIndex: gantt.length + offset - (index + projectIndex),
                      }}
                    >
                      <Element
                        element={projectElement}
                        index={index}
                        ctx={stableCtx(projectElement.uuid, ctx)}
                        graphRef={graphRef}
                        dates={dates}
                        project={project}
                        constraints={
                          constraintsByUuid.get(projectElement.uuid) ??
                          NO_CONSTRAINTS
                        }
                        meta={getMeta(projectElement.uuid)}
                        minDate={minDate}
                      />
                    </div>
                  ),
                )}
              </React.Fragment>
            );
          }}
        </ProjectCtx>
      ))}
    </div>
  );
}

export { ElementsOverlay };
